import React, { useCallback, useRef, useState } from 'react';
import Chip from 'components/styleguide/chip/Chip';
import SelectField from 'components/styleguide/select_field/SelectField';

const styles = require('./SearchInput.module.scss');

export type Item = { value: string | number; label: string };

interface SearchInputProps {
  initialItems: Item[];
  options?: Item[];
  loadOptions?: (inputValue: string) => Promise<Item[]>;
  label?: string;
  placeholder?: string;
  required?: boolean;
  context?: 'talent' | 'company' | 'neutral';
  error?: string;
  onChange?: (items: Item[]) => void;
}

export default function SearchInput({
  initialItems,
  options,
  loadOptions,
  label,
  placeholder = 'Search...',
  required = false,
  context = 'neutral',
  error,
  onChange,
}: SearchInputProps) {
  const [items, setItems] = useState<Item[]>(initialItems);
  const itemsRef = useRef<Item[]>(initialItems);

  const updateItems = useCallback((updater: (prev: Item[]) => Item[]) => {
    const next = updater(itemsRef.current);
    itemsRef.current = next;
    setItems(next);
    onChange?.(next);
  }, [onChange]);

  const handleSelect = useCallback((option: Item | null) => {
    if (!option) return;
    updateItems(prev => [...prev, option]);
  }, [updateItems]);

  const handleRemove = useCallback((item: Item) => {
    updateItems(prev => prev.filter(i => i.value !== item.value));
  }, [updateItems]);

  const isAsync = !!loadOptions;
  const filteredOptions = options?.filter(o => !items.some(i => i.value === o.value));

  return (
    <div className={styles.container}>
      <SelectField
        label={label}
        placeholder={placeholder}
        type={isAsync ? 'async' : 'default'}
        {...(isAsync ? { loadOptions } : { options: filteredOptions })}
        onChange={handleSelect}
        value={null}
        size="M"
        context={context}
        required={required}
        error={error}
        noOptionsMessage={() => isAsync ? 'Type to search' : 'No options available'}
      />

      {items.length > 0 && (
        <div className={styles.tags}>
          {items.map(item => (
            <Chip
              key={item.value}
              variant={context === 'neutral' ? 'secondary' : 'primary'}
              iconRight="x"
              iconRightLabel={`Remove ${item.label}`}
              onIconRightClick={() => handleRemove(item)}
              context={context === 'company' ? 'company' : 'talent'}
            >
              {item.label}
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
}
